import React from 'react';
import { Avatar, Box, Chip, Stack, Tooltip, Typography } from '@mui/material';
import GroupsRoundedIcon from '@mui/icons-material/GroupsRounded';
import { useTranslation } from 'react-i18next';
import type { ChatParticipant } from '../../models/Chat';
import {
  chatAvatarColor,
  chatParticipantDisplayRole,
  chatParticipantRoleLabel,
} from '../../utils/chat';
import type { ChatAvatarMap } from '../../utils/chatAvatars';
import { chatAvatarSrc } from '../../utils/chatAvatars';
import { tutorAvatarInitials } from '../../utils/tutorAvatarInitials';

export interface ChatMemberAvatarsProps {
  members: readonly ChatParticipant[];
  avatarByUserId: ChatAvatarMap;
  /** Max face slots (faces + optional +N). Fixed footprint uses this. */
  max?: number;
  size?: number;
}

/** Fixed-width stacked faces for team/support list rows and headers. */
export const ChatMemberAvatars: React.FC<ChatMemberAvatarsProps> = ({
  members,
  avatarByUserId,
  max = 3,
  size = 36,
}) => {
  const overlap = Math.round(size * 0.58);
  const slotCount = Math.max(1, max);
  const footprintWidth = size + (slotCount - 1) * overlap;

  if (members.length === 0) {
    return (
      <Box sx={{ width: footprintWidth, height: size, flexShrink: 0 }}>
        <Avatar
          sx={{
            width: size,
            height: size,
            bgcolor: 'rgba(78, 103, 235, 0.22)',
            color: 'primary.light',
          }}
        >
          <GroupsRoundedIcon sx={{ fontSize: size * 0.55 }} />
        </Avatar>
      </Box>
    );
  }

  const hasOverflow = members.length > max;
  // Keep one slot for +N when overflowing so the footprint never grows.
  const faceLimit = hasOverflow ? Math.max(1, max - 1) : Math.min(members.length, max);
  const faces = members.slice(0, faceLimit);
  const overflowCount = members.length - faces.length;

  return (
    <Box
      sx={{
        position: 'relative',
        width: footprintWidth,
        height: size,
        flexShrink: 0,
      }}
      aria-label={`${members.length} members`}
    >
      {faces.map((member, index) => {
        const label = member.name?.trim() || member.userId;
        return (
          <Tooltip key={member.userId} title={label}>
            <Avatar
              alt={label}
              src={chatAvatarSrc(avatarByUserId, member.userId)}
              sx={{
                position: 'absolute',
                left: index * overlap,
                top: 0,
                zIndex: faces.length - index,
                width: size,
                height: size,
                fontSize: size * 0.32,
                fontWeight: 700,
                border: 2,
                borderColor: 'background.paper',
                bgcolor: chatAvatarColor(member.userId),
              }}
            >
              {tutorAvatarInitials(label)}
            </Avatar>
          </Tooltip>
        );
      })}
      {overflowCount > 0 && (
        <Avatar
          sx={{
            position: 'absolute',
            left: faces.length * overlap,
            top: 0,
            zIndex: 0,
            width: size,
            height: size,
            fontSize: size * 0.3,
            fontWeight: 700,
            border: 2,
            borderColor: 'background.paper',
            bgcolor: 'rgba(78, 103, 235, 0.28)',
            color: 'primary.light',
          }}
        >
          +{overflowCount}
        </Avatar>
      )}
    </Box>
  );
};

export interface ChatMemberChipsProps {
  members: readonly ChatParticipant[];
  avatarByUserId: ChatAvatarMap;
  currentUserId?: string;
  maxVisible?: number;
}

/** Compact member chips under a team chat header. */
export const ChatMemberChips: React.FC<ChatMemberChipsProps> = ({
  members,
  avatarByUserId,
  currentUserId,
  maxVisible = 6,
}) => {
  const { t } = useTranslation();
  const ordered = [...members].sort((a, b) => {
    if (a.userId === currentUserId) return -1;
    if (b.userId === currentUserId) return 1;
    return (a.name ?? a.userId).localeCompare(b.name ?? b.userId);
  });
  const visible = ordered.slice(0, maxVisible);
  const overflow = ordered.length - visible.length;

  return (
    <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap" sx={{ mt: 0.75, mb: 0.25 }}>
      {visible.map((member) => {
        const label = member.name?.trim() || member.userId;
        const isYou = member.userId === currentUserId;
        const displayRole = chatParticipantDisplayRole(member);
        const roleLabel = displayRole ? chatParticipantRoleLabel(displayRole, t) : null;
        const chipLabel = roleLabel ? `${label} · ${roleLabel}` : label;
        return (
          <Chip
            key={member.userId}
            size="small"
            avatar={
              <Avatar
                src={chatAvatarSrc(avatarByUserId, member.userId)}
                sx={{ bgcolor: chatAvatarColor(member.userId), fontSize: '0.65rem', fontWeight: 700 }}
              >
                {tutorAvatarInitials(label)}
              </Avatar>
            }
            label={chipLabel}
            sx={{
              height: 26,
              maxWidth: 200,
              bgcolor: isYou ? 'rgba(78, 103, 235, 0.16)' : 'rgba(255,255,255,0.06)',
              border: 1,
              borderColor: isYou ? 'rgba(78, 103, 235, 0.35)' : 'divider',
              '& .MuiChip-label': {
                px: 0.75,
                fontSize: '0.75rem',
                fontWeight: isYou ? 650 : 500,
              },
              '& .MuiChip-avatar': { ml: 0.35, width: 20, height: 20 },
            }}
          />
        );
      })}
      {overflow > 0 && (
        <Box
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            px: 1,
            height: 26,
            borderRadius: 2,
            border: 1,
            borderColor: 'divider',
            bgcolor: 'rgba(255,255,255,0.04)',
          }}
        >
          <Typography variant="caption" color="text.secondary" fontWeight={600}>
            +{overflow}
          </Typography>
        </Box>
      )}
    </Stack>
  );
};
